import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Zap, Share2 } from 'lucide-react';
import { BrandLogoLink } from '../components/Layout/BrandLogoLink';
import { BrandIcon } from '../components/Layout/BrandIcon';
import { useTranslation } from 'react-i18next';
import { apiService, generateBrowserFingerprint } from '../services/api';
import { AppPageShell } from '../components/Layout/AppPageShell';
import { Footer } from '../components/Layout/Footer';
import { SettingsButton } from '../components/UI/SettingsButton';
import { ServiceUnavailable } from '../components/UI/ServiceUnavailable';
import { isConnectionError } from '../utils/apiError';

interface HomeProps {
  onRoomCreated: (roomId: string) => void;
}

const FEATURES = [
  { key: 'instantSetup', icon: Zap, accent: 'text-room-accent' },
  { key: 'realtimeSync', icon: Users, accent: 'text-room-accent' },
  { key: 'easySharing', icon: Share2, accent: 'text-room-amber' },
] as const;

const STEPS = ['step1', 'step2', 'step3'] as const;

export function Home({ onRoomCreated }: HomeProps) {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);

  const handleCreateRoom = async () => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const response = await apiService.createRoom();
      onRoomCreated(response.room_id);
    } catch (error) {
      console.error('Failed to create room:', error);
      setCreateError(error);
      setIsCreating(false);
    }
  };

  useEffect(() => {
    generateBrowserFingerprint();
  }, []);

  return (
    <AppPageShell>
      <header className="sticky top-0 z-50 mb-8 flex items-center justify-between bg-room-bg/80 pb-3 pt-1 backdrop-blur-sm">
        <div className="flex min-w-0 items-center gap-3">
          <BrandLogoLink />
          <div className="min-w-0">
            <h1 className="truncate text-[19px] font-bold tracking-tight text-room-text">
              {t('common.appName')}
            </h1>
            <p className="truncate text-[11px] text-room-text-faint">{t('common.tagline')}</p>
          </div>
        </div>
        <SettingsButton variant="room" />
      </header>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10 text-center"
      >
        <h2 className="mb-3 text-3xl font-bold tracking-tight text-room-text sm:text-4xl">
          {t('home.title')}
        </h2>
        <p className="mx-auto mb-8 max-w-lg text-base text-room-text-dim sm:text-lg">
          {t('home.subtitle')}
        </p>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleCreateRoom}
          disabled={isCreating}
          className="inline-flex items-center gap-2.5 rounded-2xl bg-room-accent px-8 py-4 text-lg font-semibold text-[#06231D] shadow-lg shadow-room-accent/20 transition-opacity hover:opacity-95 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent"
        >
          {isCreating ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#06231D]/30 border-t-[#06231D]" />
              {t('home.creatingRoom')}
            </>
          ) : (
            <>
              <BrandIcon size={20} />
              {t('home.createRoom')}
            </>
          )}
        </motion.button>

        {createError && isConnectionError(createError) ? (
          <ServiceUnavailable
            compact
            onRetry={handleCreateRoom}
            retrying={isCreating}
          />
        ) : null}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5 }}
        className="mb-8 grid gap-4 sm:grid-cols-3"
      >
        {FEATURES.map(({ key, icon: Icon, accent }) => (
          <div
            key={key}
            className="rounded-2xl border border-room-border bg-room-surface/80 p-5 text-center backdrop-blur-sm"
          >
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-room-accent-soft">
              <Icon className={`h-5 w-5 ${accent}`} strokeWidth={2} />
            </div>
            <h3 className="mb-1.5 text-sm font-semibold text-room-text">
              {t(`home.features.${key}.title`)}
            </h3>
            <p className="text-sm leading-relaxed text-room-text-dim">
              {t(`home.features.${key}.description`)}
            </p>
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="mb-4 rounded-3xl border border-room-border bg-room-surface p-6 sm:p-8"
      >
        <h2 className="mb-6 text-center text-xl font-bold text-room-text">
          {t('home.howItWorks.title')}
        </h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {STEPS.map((step, i) => (
            <div key={step} className="text-center">
              <div className="mx-auto mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-room-accent text-sm font-bold text-[#06231D]">
                {i + 1}
              </div>
              <h3 className="mb-1.5 text-sm font-semibold text-room-text">
                {t(`home.howItWorks.${step}.title`)}
              </h3>
              <p className="text-sm leading-relaxed text-room-text-dim">
                {t(`home.howItWorks.${step}.description`)}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      <Footer variant="room" />
    </AppPageShell>
  );
}
